import React, { useEffect, useState } from 'react';
import Button from '@mui/material/Button';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import {
  Avatar,
  Box,
  Divider,
  Grid,
  IconButton,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Stack,
  Tooltip,
  Typography,
  useTheme,
} from '@mui/material';
import { StyledPaperItem } from '../common/mui-components';
import { Teams } from '@par/shared/types/game';
import { Player, SocketCallback } from '@par/shared/common/types';
import { resizeImage } from '../api/cloudinary';
import { Socket } from 'socket.io-client';
import ClearIcon from '@mui/icons-material/Clear';
import { BottomChatWindow } from '../components/room/game/score-screen/BottomChatWindow';
import CloseFullscreenIcon from '@mui/icons-material/CloseFullscreen';
import OpenInFullIcon from '@mui/icons-material/OpenInFull';
import ShuffleIcon from '@mui/icons-material/Shuffle';
import { League } from '../components/league';
import Draggable from 'react-draggable';
import Paper, { PaperProps } from '@mui/material/Paper';
import ControlCameraIcon from '@mui/icons-material/ControlCamera';

let balanceShuffleTimeout;

function PaperComponent(props: PaperProps) {
  return <Paper {...props} style={{ margin: 0, maxHeight: '100%' }} />;
}

type PropTypes = {
  admin: boolean;
  close: () => void;
  open: boolean;
  gameId: string;
  profileId: string;
  teamPickingStatus?: {
    host?: string;
    teams: Teams;
    players: Record<string, Player>;
    league?: boolean;
    leagueSeason?: number;
  };
  socket: Socket;
  username: string;
};

export const TeamPickingDialog = ({
  admin,
  close,
  open,
  gameId,
  profileId,
  teamPickingStatus,
  socket,
  username,
}: PropTypes) => {
  const theme = useTheme();
  const [isLeague, setIsLeague] = useState(false);
  const [disableBalancedShuffle, setDisableBalancedShuffle] = useState(false);
  const [chatHistoryReceived, setChatHistoryReceived] = useState(false);
  const [shrinkModalWidth, setShrinkModalWidth] = useState(false);
  const [players, setPlayers] = useState<Record<string, Player>>();
  const [playerAvatarPublicIds, setPlayerAvatarPublicIds] = useState<
    {
      avatarPublicId: string;
      username: string;
    }[]
  >();
  const [joinedTeam, setJoinedTeam] = useState<'teamA' | 'teamB'>();
  const [unassignedPlayers, setUnassignedPlayers] = useState<Player[]>();
  const [teamAMembers, setTeamAMembers] = useState<Player[]>([
    null,
    null,
    null,
    null,
  ]);
  const [teamBMembers, setTeamBMembers] = useState<Player[]>([
    null,
    null,
    null,
    null,
  ]);
  const [chat, setChat] = useState<
    {
      color?: string;
      username: string;
      text: string;
      avatarPublicId: string;
      leftChat: boolean;
    }[]
  >([]);
  const [incomingChat, setIncomingChat] =
    useState<{ username: string; text: string; leftChat: boolean }[]>();
  const [chatMembers, setChatMembers] = useState<string[]>();

  useEffect(() => {
    if (!open) return;
    setChatHistoryReceived(false);
    setUnassignedPlayers(undefined);
    setTeamAMembers([null, null, null, null]);
    setTeamBMembers([null, null, null, null]);
    setChat([]);
    setIncomingChat(undefined);
    socket.on('teamPickingChat', setIncomingChat);
    return () => {
      socket.off('teamPickingChat');
    };
  }, [open]);

  useEffect(() => {
    if (!teamPickingStatus) return;
    const { teams, players, league } = teamPickingStatus;
    setIsLeague(league);
    setPlayers(players);
    const playersArr = Object.values(players);
    const teamAMembers = [];
    for (const pid of teams.teamA.members) {
      const found = playersArr.find((p) => p.profileId === pid);
      if (found) {
        teamAMembers.push(found);
      }
    }
    const filledTeamAMembers = teamAMembers.concat(
      Array(4 - teamAMembers.length).fill(null)
    );
    setTeamAMembers(filledTeamAMembers);
    const teamBMembers = [];
    for (const pid of teams.teamB.members) {
      const found = playersArr.find((p) => p.profileId === pid);
      if (found) {
        teamBMembers.push(found);
      }
    }
    const filledTeamBMembers = teamBMembers.concat(
      Array(4 - teamBMembers.length).fill(null)
    );
    setTeamBMembers(filledTeamBMembers);
    const unassignedPlayers = playersArr.filter(
      (p) =>
        !teams.teamA.members.includes(p.profileId) &&
        !teams.teamB.members.includes(p.profileId)
    );
    setUnassignedPlayers(unassignedPlayers);
  }, [teamPickingStatus]);

  useEffect(() => {
    if (!players) return;
    const playerUsernames = Object.values(players).map((p) => p.username);
    setChatMembers(playerUsernames);
    const playerAvatarPublicIds = Object.values(players).map((p) => ({
      avatarPublicId: p.avatarPublicId,
      username: p.username,
    }));

    setPlayerAvatarPublicIds(playerAvatarPublicIds);
  }, [players]);

  useEffect(() => {
    if (!playerAvatarPublicIds || chatHistoryReceived) return;
    socket.emit('requestChatHistory', {
      gameId,
      profileId,
      teamPickingChat: true,
    });
    setChatHistoryReceived(true);
  }, [playerAvatarPublicIds]);

  useEffect(() => {
    if (!incomingChat || !players || !playerAvatarPublicIds) return;
    const chatWithAvatars = incomingChat.map((c) => ({
      ...c,
      avatarPublicId:
        playerAvatarPublicIds.find((u) => u.username === c.username)
          ?.avatarPublicId || '',
    }));
    setChat(chatWithAvatars);
  }, [incomingChat]);

  const handleJoinTeamClick = (
    event: React.MouseEvent<HTMLDivElement, MouseEvent>,
    team: 'teamA' | 'teamB'
  ) => {
    if (isLeague) return;
    setJoinedTeam(team);
    socket.emit('joinTeam', {
      gameId,
      profileId,
      team,
    });
  };

  const handleLeaveTeamClick = (team: 'teamA' | 'teamB') => {
    if (isLeague) return;
    socket.emit('leaveTeam', {
      gameId,
      profileId,
      team,
    });
  };

  const userDisplay = (player: Player) => {
    return (
      <Stack direction={'row'} spacing={1} width={'100%'} alignItems={'center'}>
        <Avatar
          src={resizeImage(player.avatarPublicId, 80, 80)}
          variant="rounded"
          sx={{
            height: 25,
            width: 25,
          }}
        />
        <Typography
          fontWeight={600}
          textAlign={'left'}
          width="100%"
          sx={{
            textOverflow: 'ellipsis',
            overflow: 'hidden',
            whiteSpace: 'pre',
          }}
        >
          {player.username}{' '}
          {player.profileId === teamPickingStatus?.host ? '(host)' : ''}
        </Typography>
        {player.confirmedReady ? (
          <Typography
            variant="subtitle2"
            color="success.main"
            textAlign={'right'}
          >
            Ready
          </Typography>
        ) : null}
      </Stack>
    );
  };

  const handleLeaveGameClick = () => {
    socket.emit('leaveGame', {
      gameId,
      profileId,
    });
    close();
  };

  const isUnassigned = () => {
    if (!unassignedPlayers) return;
    const unassignedPlayersProfileIds = unassignedPlayers.map(
      (p) => p.profileId
    );
    if (unassignedPlayersProfileIds.includes(profileId)) {
      return true;
    }
    return false;
  };

  const handleTeamReadyClick = () => {
    socket.emit('confirmedTeamReady', { gameId, profileId, team: joinedTeam });
  };

  const handleStartTeamGameClick = () => {
    socket.emit('startTeamGame', { gameId, profileId });
  };

  const allPlayersReady = () => {
    if (!teamPickingStatus) return false;
    const { players } = teamPickingStatus;
    const readyArr = Object.values(players).filter((p) => !p.confirmedReady);
    if (readyArr.length) return false;
    return true;
  };

  const isReady = () => {
    if (!teamPickingStatus || !teamPickingStatus.players) return false;
    const { players } = teamPickingStatus;
    return players[profileId]?.confirmedReady;
  };

  const canStartGame = () => {
    if (!teamPickingStatus) return false;
    if (!allPlayersReady()) return false;
    const { teams } = teamPickingStatus;
    if (!teams.teamA.members.length) return false;
    if (!teams.teamB.members.length) return false;
    if (teams.teamA.members.length + teams.teamB.members.length < 3)
      return false;
    return true;
  };

  const handleBalancedShuffleClick = () => {
    socket.emit('teamsBalancedShuffle', { gameId, profileId });
    clearTimeout(balanceShuffleTimeout);
    setDisableBalancedShuffle(true);
    balanceShuffleTimeout = setTimeout(() => {
      setDisableBalancedShuffle(false);
    }, 500);
  };

  return (
    <Draggable
      handle="#draggable-dialog-title"
      cancel={'[class*="MuiDialogContent-root"]'}
    >
      <Dialog
        PaperComponent={PaperComponent}
        open={open}
        maxWidth={shrinkModalWidth ? 'xs' : 'md'}
        hideBackdrop
        disableEscapeKeyDown
        disableEnforceFocus
        sx={{
          top: '10%',
          left: 'calc(50% - 450px)',
          display: 'table', // this is a hacky alternative to fit-content since safari doesn't support it
          width: 'fit-content', // exactly the same size as its contents
        }}
      >
        <DialogTitle style={{ cursor: 'move' }} id="draggable-dialog-title">
          <Tooltip title="drag" placement="top">
            <ControlCameraIcon sx={{ position: 'absolute', left: 10 }} />
          </Tooltip>
          <IconButton
            onClick={() => setShrinkModalWidth((prev) => !prev)}
            sx={{ position: 'absolute', right: 10 }}
          >
            {shrinkModalWidth ? <OpenInFullIcon /> : <CloseFullscreenIcon />}
          </IconButton>
          <Typography
            variant="h5"
            textAlign={'center'}
            letterSpacing={5}
            sx={{
              textShadow: `0 0 4px ${theme.palette.primary.main},
0 0 4px ${theme.palette.primary.main},
0px 0px 16px ${theme.palette.primary.main}`,
            }}
          >
            PICK TEAMS
          </Typography>
        </DialogTitle>
        <DialogContent>
          <Box
            sx={{
              maxHeight: '70vh',
              overflow: 'auto',
            }}
          >
            <Grid container spacing={2} className="CONTAINER-MAIN">
              <Grid item xs={6} sm={12}>
                <StyledPaperItem>
                  <Stack direction="row" spacing={2}>
                    <Stack spacing={2} width="50%">
                      <Typography
                        variant={shrinkModalWidth ? 'subtitle2led' : 'h6led'}
                        textAlign={'center'}
                        sx={{
                          color: '#00e0ff',
                          textOverflow: 'ellipsis',
                          whiteSpace: 'pre',
                          overflow: 'hidden',
                        }}
                      >
                        Team {teamPickingStatus?.teams.teamA.username}
                      </Typography>
                      <Box
                        sx={{
                          width: '100%',
                          bgcolor: '#131d22',
                        }}
                      >
                        <List component="nav" disablePadding>
                          {!isLeague &&
                            [0, 1, 2, 3].map((i) => (
                              <>
                                <ListItem
                                  secondaryAction={
                                    teamAMembers[i] &&
                                    teamAMembers[i].profileId === profileId ? (
                                      <IconButton
                                        edge="end"
                                        onClick={() =>
                                          handleLeaveTeamClick('teamA')
                                        }
                                      >
                                        <ClearIcon color="error" />
                                      </IconButton>
                                    ) : null
                                  }
                                  disablePadding
                                >
                                  <ListItemButton
                                    dense={shrinkModalWidth}
                                    onClick={(event) =>
                                      handleJoinTeamClick(event, 'teamA')
                                    }
                                  >
                                    <ListItemText
                                      primary={
                                        teamAMembers[i] ? (
                                          userDisplay(teamAMembers[i])
                                        ) : (
                                          <Typography
                                            variant={
                                              shrinkModalWidth
                                                ? 'subtitle2'
                                                : 'body1'
                                            }
                                            letterSpacing={2}
                                            color="gray"
                                          >
                                            JOIN
                                          </Typography>
                                        )
                                      }
                                      sx={{ textAlign: 'center' }}
                                    />
                                  </ListItemButton>
                                </ListItem>
                                {i < 3 ? <Divider /> : null}
                              </>
                            ))}
                          {isLeague &&
                            teamAMembers.map((p, i) => (
                              <>
                                <ListItem disablePadding>
                                  <ListItemButton
                                    onClick={(event) =>
                                      handleJoinTeamClick(event, 'teamA')
                                    }
                                  >
                                    <ListItemText
                                      primary={
                                        p ? (
                                          userDisplay(p)
                                        ) : (
                                          <Typography
                                            variant={
                                              shrinkModalWidth
                                                ? 'subtitle2'
                                                : 'body1'
                                            }
                                            letterSpacing={2}
                                            color="gray"
                                          >
                                            RESERVED
                                          </Typography>
                                        )
                                      }
                                      sx={{ textAlign: 'center' }}
                                    />
                                  </ListItemButton>
                                </ListItem>
                                {i < 3 ? <Divider /> : null}
                              </>
                            ))}
                        </List>
                      </Box>
                    </Stack>
                    <Stack spacing={2} width="50%">
                      <Typography
                        variant={shrinkModalWidth ? 'subtitle2led' : 'h6led'}
                        textAlign={'center'}
                        sx={{
                          color: '#ec9bff',
                          textOverflow: 'ellipsis',
                          whiteSpace: 'pre',
                          overflow: 'hidden',
                        }}
                      >
                        Team {teamPickingStatus?.teams.teamB.username}
                      </Typography>
                      <Box
                        sx={{
                          width: '100%',
                          bgcolor: '#131d22',
                        }}
                      >
                        <List component="nav" disablePadding>
                          {!isLeague &&
                            [0, 1, 2, 3].map((i) => (
                              <>
                                <ListItem
                                  secondaryAction={
                                    teamBMembers[i] &&
                                    teamBMembers[i].profileId === profileId ? (
                                      <IconButton
                                        edge="end"
                                        onClick={() =>
                                          handleLeaveTeamClick('teamB')
                                        }
                                      >
                                        <ClearIcon color="error" />
                                      </IconButton>
                                    ) : null
                                  }
                                  disablePadding
                                >
                                  <ListItemButton
                                    dense={shrinkModalWidth}
                                    onClick={(event) =>
                                      handleJoinTeamClick(event, 'teamB')
                                    }
                                  >
                                    <ListItemText
                                      primary={
                                        teamBMembers[i] ? (
                                          userDisplay(teamBMembers[i])
                                        ) : (
                                          <Typography
                                            variant={
                                              shrinkModalWidth
                                                ? 'subtitle2'
                                                : 'body1'
                                            }
                                            letterSpacing={2}
                                            color="gray"
                                          >
                                            JOIN
                                          </Typography>
                                        )
                                      }
                                      sx={{ textAlign: 'center' }}
                                    />
                                  </ListItemButton>
                                </ListItem>
                                {i < 3 ? <Divider /> : null}
                              </>
                            ))}
                          {isLeague &&
                            teamBMembers.map((p, i) => (
                              <>
                                <ListItem disablePadding>
                                  <ListItemButton
                                    onClick={(event) =>
                                      handleJoinTeamClick(event, 'teamA')
                                    }
                                  >
                                    <ListItemText
                                      primary={
                                        p ? (
                                          userDisplay(p)
                                        ) : (
                                          <Typography
                                            variant={
                                              shrinkModalWidth
                                                ? 'subtitle2'
                                                : 'body1'
                                            }
                                            letterSpacing={2}
                                            color="gray"
                                          >
                                            RESERVED
                                          </Typography>
                                        )
                                      }
                                      sx={{ textAlign: 'center' }}
                                    />
                                  </ListItemButton>
                                </ListItem>
                                {i < 3 ? <Divider /> : null}
                              </>
                            ))}
                        </List>
                      </Box>
                    </Stack>
                  </Stack>
                </StyledPaperItem>
              </Grid>
              {!isLeague ? (
                <>
                  <Grid item xs={12} sm={12}>
                    <Button
                      variant="outlined"
                      onClick={handleBalancedShuffleClick}
                      startIcon={<ShuffleIcon />}
                      disabled={
                        teamPickingStatus?.host !== profileId ||
                        disableBalancedShuffle
                      }
                    >
                      Balanced Shuffle (host only)
                    </Button>
                  </Grid>
                  <Grid item xs={12} sm={12}>
                    <StyledPaperItem>
                      <Stack spacing={2}>
                        <Typography textAlign={'center'} fontWeight={600}>
                          Unassigned Players ({unassignedPlayers?.length})
                        </Typography>
                        {!shrinkModalWidth ? (
                          <>
                            <Stack direction={'row'} spacing={5}>
                              {unassignedPlayers
                                ?.slice(0, 4)
                                .map((p) => userDisplay(p))}
                            </Stack>
                            <Stack direction={'row'} spacing={5}>
                              {unassignedPlayers
                                ?.slice(4, 8)
                                .map((p) => userDisplay(p))}
                            </Stack>
                          </>
                        ) : null}
                      </Stack>
                    </StyledPaperItem>
                  </Grid>
                </>
              ) : null}
              <Grid item xs={12} sm={12}>
                <BottomChatWindow
                  chat={chat}
                  chatMembers={chatMembers}
                  gameId={gameId}
                  isDrawerOpen
                  isTeamPickingChat
                  openedBottomDrawerByClick={false}
                  small={shrinkModalWidth}
                  socket={socket}
                  title={'CHAT'}
                  username={username}
                />
              </Grid>
            </Grid>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button variant="text" fullWidth onClick={handleLeaveGameClick}>
            LEAVE GAME
          </Button>
          <Button
            variant="contained"
            fullWidth
            disabled={isUnassigned() || isReady()}
            onClick={handleTeamReadyClick}
          >
            {isUnassigned() ? 'PICK A TEAM' : 'READY TO PLAY'}
          </Button>
          {teamPickingStatus?.host === profileId ? (
            <Button
              variant="contained"
              fullWidth
              color="success"
              disabled={!canStartGame()}
              onClick={handleStartTeamGameClick}
            >
              START GAME (host)
            </Button>
          ) : null}
        </DialogActions>
      </Dialog>
    </Draggable>
  );
};
